import { z } from "zod";

// API keys: what an organization hands other software so that it can call Ravnar's API on the organization's behalf.
// Owners and admins create and revoke them; the key itself is shown once, in the answer that created it, and Ravnar
// keeps only its SHA-256. The API and the frontend share these.

/** How many API keys an organization may have at once; revoking one makes room for another. */
export const API_KEY_LIMIT = 3;

/** What every API key starts with, so that one is recognizable wherever it turns up — a log, a leaked file. */
export const API_KEY_PREFIX = "rvn_";

/**
 * An API key as software sends it: the prefix, then 43 characters of base64url, which are 32 random bytes. Anything
 * else is not a key Ravnar made.
 */
export const API_KEY_PATTERN = /^rvn_[\w-]{43}$/;

/** What an owner or admin calls a key: what it is for, such as "HubSpot sync". */
export const apiKeyNameSchema = z.string().trim().min(1, "Name the key").max(60, "Use at most 60 characters");

/**
 * An API key as the API lists it. The key itself is never part of it: only the answer that created the key carries
 * that, once (`createdApiKeySchema`).
 */
export const apiKeySchema = z.object({
	id: z.string().min(1),
	name: z.string().min(1).max(60),
	/** The key's last four characters, which is all that is ever shown of it again (`apiKeyHint`). */
	lastFour: z.string().length(4),
	/** When it was created, as an ISO 8601 timestamp in UTC. */
	createdAt: z.iso.datetime(),
	/** Email of the owner or admin who created it. */
	createdBy: z.email(),
	/** When software last called the API with it, as an ISO 8601 timestamp in UTC; absent until it has. */
	lastUsedAt: z.iso.datetime().optional(),
});
export type ApiKey = z.infer<typeof apiKeySchema>;

/** What `GET /api/api-keys` answers: the organization's keys, the oldest first. */
export const apiKeysSchema = z.object({ keys: z.array(apiKeySchema).max(API_KEY_LIMIT) });
export type ApiKeys = z.infer<typeof apiKeysSchema>;

/** What an owner or admin sends to create a key. */
export const createApiKeyRequestSchema = z.object({ name: apiKeyNameSchema });
export type CreateApiKeyRequest = z.infer<typeof createApiKeyRequestSchema>;

/** What `POST /api/api-keys` answers: the new key, and — this once and never again — the key itself. */
export const createdApiKeySchema = z.object({
	key: apiKeySchema,
	secret: z.string().regex(API_KEY_PATTERN),
});
export type CreatedApiKey = z.infer<typeof createdApiKeySchema>;

/** How a key is named wherever it is shown again: its prefix and its last four characters, "rvn_…Qm3x". */
export function apiKeyHint(key: Pick<ApiKey, "lastFour">): string {
	return `${API_KEY_PREFIX}…${key.lastFour}`;
}

/** How the audit log's name for a key's changes starts (`apiKeyActor`), which tells them apart from a person's. */
export const API_KEY_ACTOR_PREFIX = "API key ";

/**
 * Who the audit log says made a change software made with a key: "API key HubSpot sync (rvn_…Qm3x)", where a person's
 * change names their email.
 */
export function apiKeyActor(key: Pick<ApiKey, "name" | "lastFour">): string {
	return `${API_KEY_ACTOR_PREFIX}${key.name} (${apiKeyHint(key)})`;
}

/** The audit entry for a key an owner or admin created: "Created API key" / "HubSpot sync · rvn_…Qm3x". */
export function apiKeyCreatedAudit(key: Pick<ApiKey, "name" | "lastFour">): { action: string; detail: string } {
	return { action: "Created API key", detail: `${key.name} · ${apiKeyHint(key)}` };
}

/** The audit entry for a key an owner or admin revoked: "Revoked API key" / "HubSpot sync · rvn_…Qm3x". */
export function apiKeyRevokedAudit(key: Pick<ApiKey, "name" | "lastFour">): { action: string; detail: string } {
	return { action: "Revoked API key", detail: `${key.name} · ${apiKeyHint(key)}` };
}
